import { StatisticSize } from '../types';

export const CURRENCY_OPTIONS = ['USD', 'EUR', 'GBP', 'INR'] as const;

export const LOCALE_OPTIONS = [
  'en-gb',
  'en-us',
  'en-in',
  'es',
  'pt',
  'ja',
  'hi',
] as const;

export const BUTTON_ICON_POSITION = ['start', 'end'] as const;

export const KPI_CARD_SIZES: Record<
  StatisticSize,
  {
    cardPadding: number;
    // Typography
    labelFontSize: string;
    valueFontSize: string;
    subValueFontSize: string;
    // Icons
    iconSize: number;
    infoIconSize: number;
    // Gaps
    iconGap: number;
    itemGap: number;
  }
> = {
  sm: {
    cardPadding: 3,
    labelFontSize: '0.75rem', // 12px
    valueFontSize: '1rem', // 16px
    subValueFontSize: '0.75rem',
    iconSize: 24,
    infoIconSize: 14,
    iconGap: 2,
    itemGap: 1,
  },
  md: {
    cardPadding: 4,
    labelFontSize: '0.875rem', // 14px
    valueFontSize: '1.5rem', // 24px
    subValueFontSize: '0.875rem',
    iconSize: 30,
    infoIconSize: 14,
    iconGap: 3,
    itemGap: 1.5,
  },
  lg: {
    cardPadding: 5,
    labelFontSize: '1rem', // 16px
    valueFontSize: '2rem', // 32px
    subValueFontSize: '1rem',
    iconSize: 40,
    infoIconSize: 20,
    iconGap: 4,
    itemGap: 2,
  },
};

export const DELTA_CARD_SIZES: Record<
  StatisticSize,
  {
    cardPadding: number;
    labelFontSize: string;
    valueFontSize: string;
    tagFontSize: string;
    infoIconSize: number;
    itemGap: number;
    tagGap: number;
  }
> = {
  sm: {
    cardPadding: 3,
    labelFontSize: '0.75rem',
    valueFontSize: '1rem',
    tagFontSize: '0.75rem',
    infoIconSize: 14,
    itemGap: 1,
    tagGap: 5,
  },
  md: {
    cardPadding: 4,
    labelFontSize: '0.875rem',
    valueFontSize: '1.5rem',
    tagFontSize: '0.875rem',
    infoIconSize: 14,
    itemGap: 1.5,
    tagGap: 7.5,
  },
  lg: {
    cardPadding: 5,
    labelFontSize: '1rem',
    valueFontSize: '2rem',
    tagFontSize: '1rem',
    infoIconSize: 20,
    itemGap: 2,
    tagGap: 10,
  },
};

export const COMPARISON_CARD_SIZES: Record<
  StatisticSize,
  {
    cardPadding: number;
    sectionGap: number;
    titleFontSize: string;
    labelFontSize: string;
    metricFontSize: string;
    tagFontSize: string;
    iconSize: number;
    infoIconSize: number;
    itemGap: number;
    metricGap: number;
  }
> = {
  sm: {
    cardPadding: 3,
    sectionGap: 1,
    titleFontSize: '0.75rem',
    labelFontSize: '1rem',
    metricFontSize: '0.75rem',
    tagFontSize: '0.75rem',
    iconSize: 32,
    infoIconSize: 14,
    itemGap: 1,
    metricGap: 1,
  },
  md: {
    cardPadding: 4,
    sectionGap: 1.5,
    titleFontSize: '0.875rem',
    labelFontSize: '1.125rem',
    metricFontSize: '0.875rem',
    tagFontSize: '0.875rem',
    iconSize: 38,
    infoIconSize: 14,
    itemGap: 1.5,
    metricGap: 2,
  },
  lg: {
    cardPadding: 5,
    sectionGap: 2,
    titleFontSize: '1rem',
    labelFontSize: '1.25rem',
    metricFontSize: '1rem',
    tagFontSize: '1rem',
    iconSize: 44,
    infoIconSize: 20,
    itemGap: 2,
    metricGap: 2,
  },
};

export const TREND_CARD_SIZES: Record<
  StatisticSize,
  {
    width: number;
    height: number;
    labelFontSize: string;
    valueFontSize: string;
    labelPaddingX: number;
    labelPaddingY: number;
    labelMaxWidth: string;
    infoIconSize: number;
  }
> = {
  sm: {
    width: 240,
    height: 120,
    labelFontSize: '0.75rem',
    valueFontSize: '1.5rem',
    labelPaddingX: 4,
    labelPaddingY: 2,
    labelMaxWidth: '30%',
    infoIconSize: 14,
  },
  md: {
    width: 300,
    height: 150,
    labelFontSize: '1rem',
    valueFontSize: '2rem',
    labelPaddingX: 4,
    labelPaddingY: 2,
    labelMaxWidth: '30%',
    infoIconSize: 14,
  },
  lg: {
    width: 360,
    height: 180,
    labelFontSize: '1.25rem',
    valueFontSize: '2.5rem',
    labelPaddingX: 5,
    labelPaddingY: 3,
    labelMaxWidth: '30%',
    infoIconSize: 20,
  },
};

export const GAUGE_CARD_SIZES: Record<
  StatisticSize,
  {
    gaugeWidth: number;
    cardPadding: number;
    labelFontSize: string;
    infoIconSize: number;
  }
> = {
  sm: {
    gaugeWidth: 260,
    cardPadding: 3,
    labelFontSize: '0.75rem',
    infoIconSize: 14,
  },
  md: {
    gaugeWidth: 300,
    cardPadding: 4,
    labelFontSize: '0.875rem',
    infoIconSize: 14,
  },
  lg: {
    gaugeWidth: 360,
    cardPadding: 5,
    labelFontSize: '1rem',
    infoIconSize: 20,
  },
};

export const PROGRESS_CARD_SIZES: Record<
  StatisticSize,
  {
    width: number;
    cardPadding: number;
    labelFontSize: string;
    valueFontSize: string;
    progressLabelFontSize: string;
    infoIconSize: number;
  }
> = {
  sm: {
    width: 300,
    cardPadding: 3,
    labelFontSize: '0.75rem',
    valueFontSize: '1.25rem',
    progressLabelFontSize: '0.75rem',
    infoIconSize: 14,
  },
  md: {
    width: 400,
    cardPadding: 4,
    labelFontSize: '0.875rem',
    valueFontSize: '1.5rem',
    progressLabelFontSize: '0.875rem',
    infoIconSize: 14,
  },
  lg: {
    width: 500,
    cardPadding: 5,
    labelFontSize: '1rem',
    valueFontSize: '2rem',
    progressLabelFontSize: '1rem',
    infoIconSize: 20,
  },
};

export const EMPTY_VALUE_PLACEHOLDER = '—';
